import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
} from '@mui/material';
import {
  People,
  WorkOutline,
  CheckCircle,
  PendingActions,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import partnerService from '../../services/partnerService';
import serviceRequestService from '../../services/serviceRequestService';

const StatCard = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography color="text.secondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" fontWeight="bold">
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: `${color}.light`,
            borderRadius: 2,
            p: 1.5,
          }}
        >
          {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalPartners: 0,
    verifiedPartners: 0,
    pendingRequests: 0,
    completedRequests: 0,
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Get all partners
      const partners = await partnerService.getPartners();
      const verifiedPartners = partners.filter(p => p.status === 'verified');

      // Get service requests
      const pendingRequests = await serviceRequestService.getServiceRequests({ where: { status: 'pending' } });
      const completedRequests = await serviceRequestService.getServiceRequests({ where: { status: 'completed' } });

      setStats({
        totalPartners: partners.length,
        verifiedPartners: verifiedPartners.length,
        pendingRequests: pendingRequests.length,
        completedRequests: completedRequests.length,
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold">
        Welcome back, {user?.name || 'Manager'}!
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Here's an overview of your operations
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Partners"
            value={stats.totalPartners}
            icon={<People />}
            color="primary"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified Partners"
            value={stats.verifiedPartners}
            icon={<CheckCircle />}
            color="success"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Requests"
            value={stats.pendingRequests}
            icon={<PendingActions />}
            color="warning"
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed"
            value={stats.completedRequests}
            icon={<WorkOutline />}
            color="info"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Quick Actions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Common tasks and shortcuts will appear here
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Recent Activity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Recent partner and service updates
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
